import { cn, Stack } from 'component-library';
import { FC, HTMLAttributes, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { PlotReportFactLabel, PlotReportFactLabelNameEnum, ValueClass } from '@/api/rest/resources/types/fact';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePlotGroupFilterEndYear } from '@/hooks/usePlotGroupFilterEndYear';
import { usePlotGroupFilterStartYear } from '@/hooks/usePlotGroupFilterStartYear';
import { Logger } from '@/lib/logs/logger';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { isNullish } from '@/utils/booleans';
import { formatUnit } from '@/utils/formatting';
import { printMonthYear } from '@/utils/formatting/date';
import { getColorForIndicator } from '@/utils/getColorForIndicator';

import { BenchmarkPill } from '../Pill/BenchmarkPill';
import { ValueClassPill } from '../Pill/ValueClassPill';
import { Area, Chart, ComposedChart, getXAxisProps, TooltipProps } from './components/Chart';
// import { RenderConfidenceInterval } from './ConfidenceInterval'; TODO: MVP-5260 - Bring Confidence Interval back to charts
import { CHART_ZOOM } from './components/ChartZoom';
import { CustomEstimationDot } from './components/CustomEstimationDot';
import { ChartLegendBar } from './components/LegendBar';
import {
  ChartTooltip,
  // ChartTooltipConfidenceInterval,TODO: MVP-5260 - Bring Confidence Interval back to charts
  ChartTooltipDot,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from './components/Tooltip';
import { ChartLegendBarItem } from './components/types';
import { useEstimatedDataLegendItem } from './hooks/useEstimatedDataLegendItem';
import { useYAxisDomainFromMouseWheelZoom } from './hooks/useYAxisDomainFromMouseWheelZoom';
import { NoChartData } from './NoChartData';
import { extrapolateReferenceLinesChartData, RenderReferenceLine } from './ReferenceLines';
import { ChartProps } from './types';
import {
  applyBenchmarksToChartData,
  getBenchmarkLabel,
  getChartDataFromGraphFact,
  getDataDefaultDomain,
  getXAxisDomainForHistoricGraph,
  hasInterpolatedValues,
  intrapolatedValues,
  minMaxAxisDomain,
  sortChartData,
} from './utils';
import { getChartZoomedDomain } from './utils/getChartZoomedDomain';

const biodiversityColors = getColorForIndicator(INDICATOR.PROTECTED_ON_FARM_HABITAT);

const chartColors = {
  bp: {
    stroke: biodiversityColors.stroke,
    fill: biodiversityColors.fill,
    confidenceInterval: { fill: biodiversityColors.confidenceInterval },
  },
  estimatedData: { stroke: biodiversityColors.estimatedData },
} as const;

const factNames = {
  biodiversity_percent: 'bp',
  biodiversity_buffer_zone_pct_benchmark_low: 'bp_benchmark_low',
  biodiversity_buffer_zone_pct_benchmark_moderate: 'bp_benchmark_moderate',
  biodiversity_buffer_zone_pct_benchmark_high: 'bp_benchmark_high',
};

const unit = formatUnit(UnitEnum['%']);

const zoomMultipliers = {
  min: 0,
  max: 1,
};

export const BiodiversityZoneChart: FC<
  HTMLAttributes<HTMLDivElement> &
    ChartProps & {
      customBenchmark?: CustomBenchmarkSuccessResponse | null;
    }
> = ({ data, benchmarks, customBenchmark, className, showTooltip, zoom, setZoom, ...delegated }) => {
  const { t } = useTranslation();
  const filtersStartYear = usePlotGroupFilterStartYear();
  const limitMaxYear = usePlotGroupFilterEndYear();

  const styles = cn('flex w-full flex-col', className);

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const dataDomain = getDataDefaultDomain(data, zoomMultipliers.min, zoomMultipliers.max);
  const biodiversityDefaultDomain = [0, Math.min(100, 40 + dataDomain[1])] as [number, number];
  const yAxisDomainFromMouseWheel = useYAxisDomainFromMouseWheelZoom(
    chartContainerRef,
    biodiversityDefaultDomain,
    setZoom,
  );

  const dateDataMap = getChartDataFromGraphFact(data, factNames);
  const sortedChartData = sortChartData(dateDataMap, (date) => new Date(date).getTime());
  const interpolatedData = intrapolatedValues(sortedChartData, 'bp');

  const firstTick = sortedChartData.at(0);
  const lastTick = sortedChartData.at(-1);

  if (!firstTick || !lastTick) {
    Logger.error('Not enough data to render chart');
    return (
      <div className={cn(styles, 'h-full justify-center')} {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const xTicksDomain = getXAxisDomainForHistoricGraph(firstTick.label, lastTick.label, filtersStartYear, limitMaxYear);
  const xAxisProps = getXAxisProps(xTicksDomain);

  const chartData = applyBenchmarksToChartData(
    interpolatedData,
    customBenchmark,
    xTicksDomain,
    {
      low: 'bp_benchmark_low',
      moderate: 'bp_benchmark_moderate',
      high: 'bp_benchmark_high',
    },
    extrapolateReferenceLinesChartData,
  );

  const hasInterpolated = hasInterpolatedValues(chartData, 'bp');

  const yAxisDomainFromButtons = getChartZoomedDomain({
    data,
    zoom,
    defaultDomain: ([, dataMax]) =>
      minMaxAxisDomain(zoomMultipliers.min, zoomMultipliers.max)([0, Math.min(100, 40 + dataMax)]),
  });

  const hasLowData = chartData.some((point) => !!point.bp_benchmark_low);
  const hasModerateData = chartData.some((point) => !!point.bp_benchmark_moderate);
  const hasHighData = chartData.some((point) => !!point.bp_benchmark_high);

  const yAxisDomain = zoom === CHART_ZOOM.MOUSE_WHEEL ? yAxisDomainFromMouseWheel : yAxisDomainFromButtons;

  return (
    <>
      <Chart.Container
        ref={chartContainerRef}
        {...delegated}
        data={chartData}
        config={{
          bp: {
            // eslint-disable-next-line sonarjs/no-duplicate-string
            label: t('global.analysis.protectedOnFarmHabitat'),
            color: chartColors.bp.stroke,
          },
        }}
      >
        <ComposedChart data={chartData}>
          <defs>
            <linearGradient id='bp' x1='0' y1='1' x2='0' y2='0'>
              <stop offset='4.32%' stopColor={chartColors.bp.stroke} stopOpacity={0} />
              <stop offset='128.14%' stopColor={chartColors.bp.fill} stopOpacity={0.3} />
            </linearGradient>
          </defs>
          {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
          {/* {RenderConfidenceInterval({
            dataKey: 'bp.confidence_interval',
            type: 'monotone',
            fill: chartColors.bp.confidenceInterval.fill,
            id: 'bp-confidence-interval',
          })} */}
          <Area
            type='monotone'
            stroke='var(--color-bp)'
            strokeWidth={2}
            fill='url(#bp)'
            fillOpacity={1}
            dataKey='bp.value'
            connectNulls
            activeDot={ChartTooltipDot}
            dot={(props) => <CustomEstimationDot {...props} color={chartColors.estimatedData.stroke} />}
            id='bp-area'
          />
          {RenderReferenceLine({
            dataKey: 'bp_benchmark_low.value',
            type: 'monotone',
            label: getBenchmarkLabel(hasLowData, t('global.benchmark.low'), customBenchmark?.name),
            chartData,
            id: 'bp-benchmark-low',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: 'bp_benchmark_moderate.value',
            type: 'monotone',
            label: getBenchmarkLabel(hasModerateData, t('global.benchmark.moderate'), customBenchmark?.name),
            chartData,
            id: 'bp-benchmark-moderate',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: 'bp_benchmark_high.value',
            type: 'monotone',
            label: getBenchmarkLabel(hasHighData, t('global.benchmark.high'), customBenchmark?.name),
            chartData,
            id: 'bp-benchmark-high',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          <Chart.XAxis {...xAxisProps} className='bp-x-axis' />
          <Chart.YAxis domain={yAxisDomain} allowDataOverflow={true} className='bp-y-axis'>
            <Chart.Label>{formatUnit(UnitEnum['%'])}</Chart.Label>
          </Chart.YAxis>
          {showTooltip && <Chart.Tooltip content={(props) => <CustomTooltip {...props} benchmarks={benchmarks} />} />}
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar showEstimate={hasInterpolated} />
    </>
  );
};

const CustomLegendBar = ({ showEstimate }: { showEstimate: boolean }) => {
  const { t } = useTranslation();

  const estimatedDataLegendItem = useEstimatedDataLegendItem({
    estimatedDataColor: chartColors.estimatedData.stroke ?? '',
  });

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t('global.analysis.protectedOnFarmHabitat'),
      color: chartColors.bp.fill,
    },
    ...(showEstimate ? estimatedDataLegendItem : []),
  ];

  return (
    <ChartLegendBar items={legendBarItems} className='sm:ml-[28px]' data-testid='biodiversity-zone-chart-legend' />
  );
};

const CustomTooltip = ({
  active,
  payload,
  label,
  benchmarks,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
TooltipProps<ValueType, NameType> & { benchmarks?: any[] }) => {
  const { t } = useTranslation();

  const bpValue = payload?.find((data) => data.dataKey === 'bp.value');
  const bpDisplay = !isNullish(bpValue?.value)
    ? `${getDisplayNumber(bpValue?.value as string, window.navigator.language)} ${unit}`
    : t('global.analysis.noData');
  const bpBenchmarkType = useBenchmarkForFact(benchmarks, bpValue?.value as string);

  const dataPointHasLabel = bpValue && bpValue?.payload?.bp?.labels?.length > 0;
  const dataPointValueClass = dataPointHasLabel
    ? bpValue.payload.bp?.labels?.find(
        (dataLabel: PlotReportFactLabel) => dataLabel.name === PlotReportFactLabelNameEnum.value_class,
      )
    : undefined;

  const dataLabel = dataPointValueClass?.value as ValueClass;

  if (!active) return null;

  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printMonthYear(label)}</ChartTooltipTextSecondary>
        {dataLabel && <ValueClassPill title={dataLabel} />}
      </Stack>
      {bpBenchmarkType && (
        <ChartTooltipTextSecondary>
          <BenchmarkPill benchmarkType={bpBenchmarkType} />
        </ChartTooltipTextSecondary>
      )}
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextPrimary>{`${t('global.analysis.protectedOnFarmHabitat')}`}</ChartTooltipTextPrimary>
        <ChartTooltipTextPrimary>{bpDisplay}</ChartTooltipTextPrimary>
      </Stack>
      {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
      {/* <ChartTooltipConfidenceInterval ciPct={bpValue?.payload?.bp?.confidence_interval_pct} /> */}
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipBiodiversityZone };
